<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  message: { type: String, default: '' },
  variant: { type: String, default: 'info' },
  action: {
    type: Object,
    default: null,
  },
  dismissLabel: {
    type: String,
    default: 'Dismiss toast',
  },
});

const emit = defineEmits(['dismiss']);

const variantConfig = {
  success: {
    icon: 'i-lucide-check',
    iconClass: 'text-n-teal-9',
    glowClass: 'shadow-[0_12px_28px_-22px_rgba(20,184,166,0.85)]',
  },
  warning: {
    icon: 'i-lucide-badge-alert',
    iconClass: 'text-n-amber-9',
    glowClass: 'shadow-[0_12px_28px_-22px_rgba(245,158,11,0.85)]',
  },
  error: {
    icon: 'i-lucide-triangle-alert',
    iconClass: 'text-n-ruby-9',
    glowClass: 'shadow-[0_12px_28px_-22px_rgba(244,63,94,0.85)]',
  },
  info: {
    icon: 'i-lucide-info',
    iconClass: 'text-n-blue-9',
    glowClass: 'shadow-[0_12px_28px_-22px_rgba(59,130,246,0.75)]',
  },
};

const activeVariant = computed(
  () => variantConfig[props.variant] || variantConfig.info
);

const actionLabel = computed(
  () => props.action?.label || props.action?.message || ''
);

const isLinkAction = computed(() =>
  ['link', 'show_me'].includes(props.action?.type)
);

/**
 * Runs a button-style toast action before dismissing the toast.
 */
const runAction = () => {
  props.action?.onClick?.();
  emit('dismiss');
};
</script>

<template>
  <div
    class="mb-2 inline-flex w-full max-w-[28rem] items-center gap-2.5 rounded-xl border border-n-weak bg-white px-3 py-2 text-left shadow-lg dark:border-n-slate-6 dark:bg-n-slate-2 sm:w-auto sm:min-w-[18rem]"
    :class="activeVariant.glowClass"
  >
    <Icon
      :icon="activeVariant.icon"
      class="size-4 shrink-0"
      :class="activeVariant.iconClass"
    />

    <div class="min-w-0 flex-1 text-sm font-medium text-n-slate-12">
      {{ message }}
    </div>

    <router-link
      v-if="isLinkAction"
      data-testid="toast-action"
      :to="action.to"
      class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold text-n-blue-11 transition-colors hover:bg-n-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      @click="emit('dismiss')"
    >
      {{ actionLabel }}
    </router-link>

    <button
      v-else-if="action"
      data-testid="toast-action"
      type="button"
      class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold text-n-slate-12 transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      @click="runAction"
    >
      {{ actionLabel }}
    </button>

    <button
      data-testid="toast-dismiss"
      type="button"
      :aria-label="dismissLabel"
      class="flex size-9 shrink-0 items-center justify-center rounded-full text-n-slate-9 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      @click="emit('dismiss')"
    >
      <Icon icon="i-lucide-x" class="size-7" />
    </button>
  </div>
</template>
